import React, { FC, useMemo } from 'react'
import FroalaEditorComponent from 'react-froala-wysiwyg';
import { Snackbar } from 'neon-design-system';
import { useAuth0Data } from 'neon-auth0';
import { useTheme, ExtendedTheme, ErrorText } from 'neon-design-system';
import { makeStyles, createStyles } from '@material-ui/core';
import { useNotification } from '../../providers/NotificationProvider';
import { useLogger } from '../../providers/logger/Logger';
import { LogLevel, LogAction } from '../../providers/logger/logger-types';
import { config } from '../../../config';

// Require Editor CSS files.
import sanitizeHtml from 'sanitize-html';
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/themes/dark.min.css';
import 'froala-editor/css/plugins/line_breaker.min.css';
// Require Font Awesome.
import '@fortawesome/fontawesome-free';
// Froala Plugins
import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/char_counter.min.js';
import 'froala-editor/js/plugins/paragraph_format.min.js';
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/inline_style.min.js';
import 'froala-editor/js/plugins/file.min.js';
import 'froala-editor/js/plugins/image.min.js';
import 'froala-editor/js/plugins/draggable.min.js';
import 'froala-editor/js/plugins/video.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/js/plugins/font_size.min.js';
import 'froala-editor/js/plugins/quote.min.js';
import 'froala-editor/js/plugins/line_height.min.js';
import 'froala-editor/js/plugins/line_breaker.min.js';
import 'froala-editor/js/plugins/quick_insert.min.js';
import 'froala-editor/js/plugins/word_paste.min.js';
import 'froala-editor/js/plugins/print.min.js';
import { setBuzzsproutPlayerHeight } from '../../../utils/utils';
import { TABLEAU_ENABLED, makeCharts } from '../../../utils/tableau';
import { useApolloClient, useQuery } from '@apollo/react-hooks';
import ApolloClient from 'apollo-client';

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) => createStyles({
	errorLabel: {
		marginTop: theme.spacing(1)
	},
	editor: {
		'& .dark-theme.fr-box.fr-basic a': {
			color: theme.themeColors.secondary.main,
			textDecoration: 'none',
			'&:hover, &:focus': { textDecoration: 'none' },
			'& > u': { textDecoration: 'none' }
		},
		'& .dark-theme.fr-box.fr-basic p, & li': {
			fontSize: theme.customTypography.largeBody2.medium.fontSize
		},
		'& .fr-img-caption .fr-img-wrap > span': theme.typography.caption,
		'& .fr-img-space-wrap': theme.typography.body1,
		'& .dark-theme.fr-box.fr-basic img': {
			borderRadius: 4,
			height: 'auto'
		},
		'& .dark-theme.fr-box.fr-basic .fr-wrapper': {
			backgroundColor: theme.palette.background.default + ' !important',
			borderLeft: '2px solid ' + theme.themeColors.grey[500] + ' !important',
			borderRight: '2px solid ' + theme.themeColors.grey[500] + ' !important',
			borderBottom: '2px solid ' + theme.themeColors.grey[500] + ' !important'
		},
		'& .dark-theme.fr-box.fr-basic .fr-toolbar': {
			backgroundColor: theme.themeColors.grey[700] + ' !important',
			border: '2px solid ' + theme.themeColors.grey[500] + ' !important'
		},
		'& .dark-theme.fr-box.fr-basic .fr-command.fr-open': {
			backgroundColor: theme.themeColors.grey[800] + ' !important'
		},
		'& .dark-theme.fr-box.fr-basic .fr-more-toolbar': {
			backgroundColor: theme.themeColors.grey[800] + ' !important'
		},
		'& .dark-theme.fr-box.fr-basic .fr-second-toolbar, & .dark-theme.fr-box.fr-basic .second-toolbar': {
			backgroundColor: theme.palette.background.default + ' !important',
			borderRight: '2px solid ' + theme.themeColors.grey[500] + ' !important',
			borderBottom: '2px solid ' + theme.themeColors.grey[500] + ' !important',
			borderLeft: '2px solid ' + theme.themeColors.grey[500] + ' !important'
		},
		'& .dark-theme.fr-box.fr-basic .fr-element': {
			fontFamily: '"Inter" !important',
			fontSize: theme.customTypography.largeBody2.medium.fontSize,
			color: theme.palette.text.primary,
			maxWidth: 1000,
			margin: '0 auto',
			padding: theme.spacing(0, 4)
		},
		'& .dark-theme.fr-box.fr-basic .fr-placeholder': {
			color: theme.palette.text.secondary + ' !important'
		},
		'& .dark-theme .fr-view hr': {
			border: 0,
			backgroundColor: theme.themeColors.grey[600],
			height: 2,
			margin: theme.spacing(2, 0)
		},
		'& .promotional-content': {
			display: 'block',
			margin: `${theme.spacing(2)} 0`,
			pointerEvents: 'none'
		},
		'& .promotional-content > img': {
			display: 'block',
			width: '100%',
			height: 'auto',
			borderRadius: '8px',
			pointerEvents: 'none'
		},
		[theme.breakpoints.down('md')]: {
			'& .promotional-content': { display: 'none' }
		},
		'& .fr-more-toolbar': { bottom: -1 }
	}
}));

export interface EditorProps {
	initialHtml: string;
	onChange: (value: string) => void;
	errorMessage?: string;
	customOptions?: any;
	className?: string;
}

let lastUploadedFilename = '';
let lastUploadedImageName = '';

//https://www.froala.com/wysiwyg-editor/docs/options
const editorConfig = (client: ApolloClient<object>, theme: ExtendedTheme, onError: (msg: string | JSX.Element) => void, logger: any, customOptions: any = {}) => {
	const { events, ...restCustomOptions } = customOptions;

	return {
		key: 'dKA5cC2C2F2G2F1B5zPAENHMi1JPQRFZBTBa1WWEPYDbA2B6C4B3F4B2F3C3H2E2== ',
		theme: 'dark',
		attribution: false,
		heightMin: 600,
		placeholderText: 'Start typing or insert rich content using the above toolbar.',
		pastePlain: false,
		toolbarSticky: true,
		toolbarStickyOffset: theme.spacing(6),
		paragraphFormat: {
			N: 'Normal text',
			H1: 'Heading 1',
			H2: 'Heading 2',
			H3: 'Heading 3'
		},
		pasteDeniedAttrs: ['style', 'script'],
		htmlRemoveTags: ['table', 'th', 'tr', 'td', 'tbody', 'thead', 'tfoot', 'caption', 'col', 'colgroup'],
		fileUpload: true,
		fileAllowedTypes: ['application/pdf'],
		fileUploadURL: config.serverUrl + '/upload-file',
		fileMaxSize: 20 * 1024 * 1024,
		listAdvancedTypes: false,
		imageUploadURL: config.serverUrl + '/upload',
		imageAllowedTypes: ['jpeg', 'jpg', 'png', 'gif'],
		imageSplitHTML: true,
		imageMove: true,
		imageDefaultAlign: 'center',
		imageDefaultWidth: 0,
		imageInsertButtons: ['imageBack', '|', 'imageUpload', 'imageByURL'],
		imageEditButtons: ['imageAlign', 'imageDisplay', 'imageCaption', 'imageRemove'],
		requestHeaders: {
			Authorization: ''
		},
		linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove'],
		linkInsertButtons: ['linkBack'],
		quickInsertButtons: ['image', 'video', 'ul', 'ol', 'hr'],
		toolbarButtons: {
			'style': {
				'buttons': ['paragraphFormat']
			},
			'moreText': {
				'buttons': ['bold', 'italic', 'underline', 'alignLeft', 'alignCenter', 'alignRight', 'inlineStyle', 'textColor', 'clearFormatting', 'outdent', 'indent']
			},
			'moreLists': {
				'buttons': ['formatOL', 'formatUL']
			},
			'moreRich': {
				'buttons': ['insertImage', 'insertLink', 'insertVideo', 'insertHR', 'promotionalContentDropdown', 'tableauCharts', 'getPDF'],
				'buttonsVisible': 7
			},
			'moreMisc': {
				'buttons': ['undo', 'redo'],
				'align': 'right'
			}
		},
		toolbarButtonsXS: {
			'moreText': {
				'buttons': ['paragraphFormat', 'formatOL', 'formatUL', 'bold', 'italic', 'underline', 'alignLeft', 'alignCenter', 'alignRight', 'inlineStyle', 'textColor', 'clearFormatting', 'outdent', 'indent']
			},
			'moreRich': {
				'buttons': ['insertImage', 'insertLink', 'insertVideo', 'insertHR', 'promotionalContentDropdown', 'getPDF'],
				'buttonsVisible': 2
			},
			'moreMisc': {
				'buttons': ['undo', 'redo'],
				'align': 'right'
			}
		},
		videoAllowedProviders: ['vimeo', 'spotify', 'buzzsprout'],
		videoInsertButtons: ['videoByURL', 'videoEmbed'],
		videoResponsive: true,
		wordPasteModal: false,
		wordPasteKeepFormatting: false,
		colorsStep: 8,
		colorsText: [
			theme.palette.common.white, theme.themeColors.blueGrey[200], theme.themeColors.info.light, theme.themeColors.secondary.light, theme.themeColors.success.light, theme.themeColors.warning.light, theme.themeColors.error.light, theme.themeColors.primary.light,
			theme.themeColors.grey[200], theme.themeColors.blueGrey[400], theme.themeColors.info.main, theme.themeColors.secondary.main, theme.themeColors.success.main, theme.themeColors.warning.main, theme.themeColors.error.main, theme.themeColors.primary.main,
			theme.themeColors.grey[400], theme.themeColors.blueGrey[600], theme.themeColors.info.dark, theme.themeColors.secondary.dark, theme.themeColors.success.dark, theme.themeColors.warning.dark, theme.themeColors.error.dark, theme.themeColors.primary.dark
		],
		inlineStyles: {
			'Text highlight - Yellow': 'color: #FFFFFF; background-color: #BB9B28;',
			'Text highlight - Orange': 'color: #FFFFFF; background-color: ' + theme.themeColors.warning.dark + ';',
			'Text highlight - Red': 'color: #FFFFFF; background-color: ' + theme.themeColors.error.dark + ';',
			'Text highlight - Purple': 'color: #FFFFFF; background-color: ' + theme.themeColors.primary.dark + ';',
			'Text highlight - Blue': 'color: #FFFFFF; background-color: ' + theme.themeColors.info.dark + ';',
			'Text highlight - Neutral': 'color: #FFFFFF; background-color: ' + theme.themeColors.blueGrey.dark + ';',
			'Text highlight - Teal': 'color: #FFFFFF; background-color: ' + theme.themeColors.secondary.dark + ';',
			'Text highlight - Green': 'color: #FFFFFF; background-color: ' + theme.themeColors.success.dark + ';'
		},
		events: {
			'image.beforeUpload': function (images: any[]) {
				lastUploadedImageName = images[0].name;
			},
			'image.error': function (error: any, response: any) {
				logger(LogLevel.WARN, LogAction.ImageUpload, 'Error', { response, error, filename: lastUploadedImageName });
			},
			'image.uploaded': function (response: any) {
				logger(LogLevel.INFO, LogAction.ImageUpload, 'Uploaded', { response, filename: lastUploadedImageName });
			},
			'file.error': function (error: any, response: any) {
				logger(LogLevel.WARN, LogAction.FileUpload, 'Error', { response, error });
			},
			'file.beforeUpload': function (files: any) {
				lastUploadedFilename = files[0].name;
			},
			'file.uploaded': function (response: any) {
				const articleId = window.location.pathname.split('/').slice(-1)?.[0];
				const links = JSON.parse(response).links;
				logger(LogLevel.INFO, LogAction.FileUpload, 'Uploaded PDF', {
					filename: lastUploadedFilename,
					articleId,
					numPages: links.length
				});

				const imagesHtml = links
					.reduce((html: string, imageUrl: string) => html + `<p><img alt="${imageUrl}" src="${imageUrl}" class="fr-fic fr-dib"></p>`, '');

				const safeImagesHtml = sanitizeHtml(imagesHtml, {
					allowedTags: ['p', 'img'],
					allowedAttributes: { 'img': ['src', 'class'] }
				});

				(this as any).html.insert(safeImagesHtml);
				(this as any).popups.hideAll()
				return false;
			},
			'initialized': function() {
				const webSpellChecker = (window as any).WEBSPELLCHECKER;

				webSpellChecker && webSpellChecker.init({
					container: this.el
				});

				setBuzzsproutPlayerHeight();

				makeCharts(client, this.el);

				const observer = new MutationObserver(() => makeCharts(client, this.el));
				observer.observe(this.el, { childList: true });
			},
			...events
		},
		...restCustomOptions
	};
}

const Editor: FC<EditorProps> = ({ initialHtml, onChange, errorMessage, customOptions, className }) => {
	const { theme } = useTheme();
	const classes = useStyles();
	const { printLog } = useLogger();
	const { showMessage, message, clearMessage, messageType } = useNotification();
	const client = useApolloClient();
	const {data} = useQuery(TABLEAU_ENABLED, {fetchPolicy: 'network-only'});
	const { accessToken } = useAuth0Data();

	const config = useMemo(() => {
		const conf = editorConfig(client, theme, showMessage, printLog, customOptions);
		conf.requestHeaders.Authorization = `Bearer ${accessToken}`;
		const isTableau = !!data?.me.tableauAccess;
		if (!isTableau && conf.toolbarButtons.moreRich) {
			conf.toolbarButtons.moreRich.buttons = conf.toolbarButtons.moreRich.buttons.filter((b: string) => b !== 'tableauCharts');
			conf.toolbarButtons.moreRich.buttonsVisible -= 1;
		}
		return conf;
	}, [accessToken, data]);

	if (!data) return <></>;

	return (
		<div className={[classes.editor, className].join(' ')}>
			<FroalaEditorComponent
				config={config}
				model={initialHtml}
				onModelChange={(v: string) => {
					const formattedValue = v.replace(/rgb\(204, 204, 204\)/gi, 'inherit');
					onChange(formattedValue);
				}}
			/>
			<Snackbar
				open={!!message}
				message={message ?? ''}
				type={messageType ?? 'error'}
				onClose={clearMessage}
				onUndo={undefined}
			/>
			<ErrorText className={classes.errorLabel}>{errorMessage}</ErrorText>
		</div>
	);
};

export default Editor;
